import { useEffect } from "react";
import { useLocation } from "react-router-dom";

import { isCognitoApiTokenEnabled } from "./cognitoApiTokenState";
import { getCognitoIdToken } from "./cognitoAuth";
import { handleCognitoSessionRevoked, isCognitoSessionRevokedError } from "./cognitoSessionLogout";

const COGNITO_STORAGE_PREFIX = "CognitoIdentityServiceProvider";

/**
 * Checks whether Cognito auth tokens exist in localStorage, indicating
 * the user has (or had) an active Cognito session.  This is a fast
 * synchronous check that avoids triggering a network call on pages
 * where the user was never logged in (login page, magic link page, etc.).
 */
function hasCognitoSession(): boolean {
  for (let index = 0; index < localStorage.length; index++) {
    const key = localStorage.key(index);
    if (key?.startsWith(COGNITO_STORAGE_PREFIX)) {
      return true;
    }
  }
  return false;
}

/**
 * Proactively verifies the Cognito session on route changes.
 *
 * Cognito's `globalSignOut` revokes the *refresh* token, but cached
 * access / id tokens remain cryptographically valid until they naturally
 * expire (up to 1 hour).  This hook forces a token refresh on every
 * navigation so that revoked sessions are detected promptly rather than
 * waiting for the access token to expire.
 *
 * The check is fire-and-forget — it does not block the navigation.
 * If the session is still valid the refresh succeeds silently.
 * If the refresh token has been revoked the error is caught and the
 * user is logged out and redirected to /enter with an informative toast.
 */
export function useCognitoSessionCheck(): void {
  const location = useLocation();

  useEffect(() => {
    if (!isCognitoApiTokenEnabled()) {
      return;
    }

    // Skip if no Cognito tokens exist in localStorage.  This prevents
    // misinterpreting "never logged in" as "session revoked" on pages
    // like /enter or /v2/email-login-link, which would otherwise cause
    // an infinite redirect loop.
    if (!hasCognitoSession()) {
      return;
    }

    // Amplify v6 catches NotAuthorizedException during token refresh and
    // returns null instead of throwing.  We must handle BOTH the resolved-
    // with-no-token path AND the thrown-error path.
    getCognitoIdToken(true)
      .then((token) => {
        if (!token) {
          void handleCognitoSessionRevoked({ source: "navigation-session-check" });
        }
      })
      .catch((error: unknown) => {
        if (isCognitoSessionRevokedError(error)) {
          void handleCognitoSessionRevoked({ error, source: "navigation-session-check" });
        }
      });
  }, [location.pathname]);
}
